/* eslint-disable complexity */
/* eslint-disable max-lines */
import { A11yModule } from '@angular/cdk/a11y';
import { LayoutModule } from '@angular/cdk/layout';
import { OverlayModule } from '@angular/cdk/overlay';
import { DatePipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { GenericAction, SelectedTab, Tab } from '@com-delta-omnichannel/mach';
import { TabItem, Tabs } from '@com-delta-omnichannel/mach-core-js';
import {
  IdpPredictiveSearchMachCoreComponent,
  PredictiveSearch
} from './components/predictive-search';
import { RecentListItem } from './models/search-modal-input.interface';

@Component({
  standalone: true,
  selector: 'idp-book-widget-search-modal-mach-core',
  styleUrls: ['book-widget-search-modal-mach-core.component.scss'],
  templateUrl: 'book-widget-search-modal-mach-core.component.html',
  imports: [
    OverlayModule,
    IdpPredictiveSearchMachCoreComponent,
    NgFor,
    NgTemplateOutlet,
    NgIf,
    LayoutModule,
    DatePipe,
    A11yModule
  ],
  encapsulation: ViewEncapsulation.None
})
export class BookWidgetSearchModalMachCoreComponent implements OnChanges {
  private itemSelected = false;
  protected selectedTabIndex = 0;
  private predictiveSearchRef!: IdpPredictiveSearchMachCoreComponent;
  valueChangeOnInput = false;
  isModalOpen = false;
  private tabsInstance!: Tabs;
  @Input() searchModalTabs: Tab[] = [];
  @Input() savedSearchList: string[] = [];
  @Input() recentSearchList: RecentListItem[] = [];
  @Input() predictiveSearchData: PredictiveSearch = {
    placeholder: '',
    items: [],
    title: '',
    value: '',
    transparentSearch: true,
    errorMessage: '',
    selectLabel: ''
  };

  @Input() isFullscreen = false;
  @Input() set modalOpen(isModalOpen: boolean) {
    this.isModalOpen = isModalOpen;
    if (isModalOpen) {
      this.initializeSearchModalTabs(
        this.searchModalTabs.map(
          (tab) =>
            ({
              id: tab.id,
              label: tab.label,
              selected: false,
              value: tab.id
            } as TabItem)
        )
      );
    }
  }

  /**
   * emits when tab is changed
   */
  @Output()
  selectedTabChange = new EventEmitter<SelectedTab>();
  /**
   * emits when search modal is closed, either by close button click,
   * or by clicking outside modal
   */

  @Output() searchModalClose = new EventEmitter<string>();
  /**
   * emits the input text when something is being typed in search input
   */
  @Output() searchStringChanged = new EventEmitter<string>();
  /**
   * emits with ListItem when dropdown item is clicked
   */
  @Output() predictiveSearchItemSelected = new EventEmitter<
    GenericAction<string>
  >();

  @Output() recentSearchItemSelected = new EventEmitter<number>();
  @Output() savedSearchItemSelected = new EventEmitter<number>();
  @ViewChild(IdpPredictiveSearchMachCoreComponent) set ref(
    predictiveSearchRef: IdpPredictiveSearchMachCoreComponent | undefined
  ) {
    if (predictiveSearchRef) {
      this.predictiveSearchRef = predictiveSearchRef;
    }
  }

  constructor(private renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes?.['searchModalTabs'] &&
      !changes?.['searchModalTabs']?.firstChange &&
      this.isModalOpen
    ) {
      this.initializeSearchModalTabs(
        this.searchModalTabs.map(
          (tab) =>
            ({
              id: tab.id,
              label: tab.label,
              selected: false,
              value: tab.id
            } as TabItem)
        )
      );
    }
  }

  valueChangeOnInputHandler() {
    this.valueChangeOnInput = true;
  }

  protected closeModal(fromCloseIcon = false) {
    this.modalOpen = false;
    this.selectedTabIndex = 0;
    let searchText = '';
    if (
      fromCloseIcon &&
      this.valueChangeOnInput &&
      this.getSelectedTabId() === 'search' &&
      this.predictiveSearchRef?.searchText?.length === 3
    ) {
      searchText = this.predictiveSearchRef.searchText;
    }
    this.valueChangeOnInput = false;
    this.searchModalClose.emit(searchText);
  }

  protected handleTabChange(tab: SelectedTab) {
    this.selectedTabIndex = tab.selectedTabIndex;
    this.selectedTabChange.emit(tab);
  }

  // This uses a flag itemSelected that prevents
  // handleValueChange from emitting when an item is being selected.
  // The flag is reset asynchronously with setTimeout to ensure it doesn't interfere with subsequent user typing.
  protected handleChoose(chosen: GenericAction<string>) {
    this.itemSelected = true;
    this.predictiveSearchItemSelected.emit(chosen);
    this.closeModal();
    setTimeout(() => (this.itemSelected = false), 0);
  }

  protected handleValueChange(searchString: string) {
    if (!this.itemSelected && searchString.length >= 3) {
      this.searchStringChanged.emit(searchString);
    }
  }

  protected handleRecentOrSavedSearchItemClick(
    type: 'saved' | 'recent',
    index: number
  ) {
    type === 'recent'
      ? this.recentSearchItemSelected.emit(index)
      : this.savedSearchItemSelected.emit(index);
    this.closeModal();
  }

  protected getSelectedTabId(): string {
    return this.searchModalTabs[this.selectedTabIndex]?.id || '';
  }

  initializeSearchModalTabs(tabItems: TabItem[]) {
    if (this.tabsInstance) {
      this.tabsInstance.destroy();
    }

    setTimeout(() => {
      const tabElementId = `airport-search-modal-tab-${Math.random()
        .toString(36)
        .substring(2, 15)}`;
      // Create the date-picker div dynamically
      const tabsElement = this.renderer.createElement('div');
      this.renderer.addClass(tabsElement, 'tabs');
      this.renderer.setAttribute(tabsElement, 'id', tabElementId);
      this.renderer.appendChild(
        document.getElementById('airport-search-modal-tabs-container'),
        tabsElement
      );

      this.tabsInstance = new Tabs({
        id: tabElementId,
        tabVariant: 'standard',
        tabSize: 'small',
        tabWidth: 'intrinsic',
        selectedIndex: this.selectedTabIndex,
        tabItems: tabItems
      });
      this.tabsInstance.selectTabByIndex(this.selectedTabIndex);
      this.setupTabChangeHandler();
    }, 0);
  }

  setupTabChangeHandler() {
    this.tabsInstance.onTabChange((selectedTabIndex, id) => {
      this.selectedTabIndex = selectedTabIndex;
      this.selectedTabChange.emit({
        selectedTabIndex,
        tab: this.searchModalTabs.find((tab) => tab.id === id) ?? ({} as Tab)
      });
    });
  }
}
